'use client';

import { useState } from 'react';
import { Download, Shield, Lock, Eye, FileText, CheckCircle, AlertTriangle, Clock, Users } from 'lucide-react';

export default function SecurityCompliancePage() {
    const [activeTab, setActiveTab] = useState<'overview' | 'hipaa' | 'certifications' | 'downloads'>('overview');

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
                <div className="max-w-7xl mx-auto px-6 py-16">
                    <div className="flex items-center space-x-4 mb-6">
                        <Shield className="h-16 w-16" />
                        <div>
                            <h1 className="text-5xl font-bold mb-2">Security & Compliance</h1>
                            <p className="text-xl text-blue-100">Your data security and privacy is our top priority</p>
                        </div>
                    </div>

                    {/* Compliance Status Banner */}
                    <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="flex items-center space-x-3">
                                <CheckCircle className="h-8 w-8 text-green-300" />
                                <div>
                                    <div className="text-sm text-blue-100">SOC 2 Type II</div>
                                    <div className="font-semibold">In Progress</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Clock className="h-8 w-8 text-yellow-300" />
                                <div>
                                    <div className="text-sm text-blue-100">HIPAA Compliance</div>
                                    <div className="font-semibold">Phase 1 Complete</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Lock className="h-8 w-8 text-blue-300" />
                                <div>
                                    <div className="text-sm text-blue-100">ISO 27001</div>
                                    <div className="font-semibold">Roadmap 2026</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-12">

                {/* Tab Navigation */}
                <div className="flex space-x-1 mb-8 bg-white rounded-lg p-1 shadow-sm border border-gray-200">
                    {[
                        { id: 'overview', label: 'Security Overview', icon: Shield },
                        { id: 'hipaa', label: 'HIPAA Compliance', icon: FileText },
                        { id: 'certifications', label: 'Certifications & Audits', icon: CheckCircle },
                        { id: 'downloads', label: 'Documentation', icon: Download },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-md font-medium transition-all ${activeTab === tab.id
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <tab.icon className="h-5 w-5" />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && <SecurityOverview />}
                {activeTab === 'hipaa' && <HIPAACompliance />}
                {activeTab === 'certifications' && <Certifications />}
                {activeTab === 'downloads' && <Downloads />}
            </div>
        </div>
    );
}

// Security Overview Tab
function SecurityOverview() {
    return (
        <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Security Framework</h2>
                <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                    At LinkBox AI, we implement industry-leading security practices to protect your data.
                    Our comprehensive security program covers all aspects of data protection, from encryption
                    to access controls to incident response.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                    <SecurityFeature
                        icon={<Lock className="h-8 w-8 text-blue-600" />}
                        title="Enterprise-Grade Encryption"
                        description="AES-256 encryption at rest, TLS 1.3 in transit. All data is encrypted using military-grade standards."
                        status="deployed"
                    />
                    <SecurityFeature
                        icon={<Eye className="h-8 w-8 text-green-600" />}
                        title="Comprehensive Audit Logging"
                        description="Immutable audit logs track all data access with 6-year retention for complete accountability."
                        status="deployed"
                    />
                    <SecurityFeature
                        icon={<Users className="h-8 w-8 text-purple-600" />}
                        title="Multi-Factor Authentication"
                        description="TOTP-based MFA required for all users accessing sensitive data."
                        status="in-progress"
                    />
                    <SecurityFeature
                        icon={<CheckCircle className="h-8 w-8 text-indigo-600" />}
                        title="Row-Level Security"
                        description="Database-level access controls ensure users can only access their own data."
                        status="deployed"
                    />
                </div>
            </div>

            {/* Security Controls */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Security Controls</h3>
                <div className="space-y-4">
                    <ControlRow label="Data Encryption at Rest" detail="AES-256" status="deployed" />
                    <ControlRow label="Data Encryption in Transit" detail="TLS 1.3" status="deployed" />
                    <ControlRow label="Session Timeout" detail="15 minutes idle" status="deployed" />
                    <ControlRow label="Password Requirements" detail="12+ characters, complexity enforced" status="deployed" />
                    <ControlRow label="Database Access Control" detail="RLS on all 194 tables" status="deployed" />
                    <ControlRow label="Immutable Audit Logs" detail="6-year retention" status="deployed" />
                    <ControlRow label="Multi-Factor Authentication" detail="TOTP (Time-based OTP)" status="in-progress" />
                    <ControlRow label="Role-Based Access Control" detail="Admin, Developer, Support, User" status="planned" />
                    <ControlRow label="End-to-End Encryption" detail="Client-side PHI encryption" status="planned" />
                </div>
            </div>

            {/* Incident Response */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Incident Response</h3>
                <p className="text-gray-700 mb-6">
                    We maintain a 24/7 incident response capability with defined escalation procedures and
                    response SLAs:
                </p>
                <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="text-red-600 font-bold text-sm mb-1">P0 - Critical</div>
                        <div className="text-2xl font-bold text-red-700">15 min</div>
                        <div className="text-sm text-red-600">Response SLA</div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="text-orange-600 font-bold text-sm mb-1">P1 - High</div>
                        <div className="text-2xl font-bold text-orange-700">1 hour</div>
                        <div className="text-sm text-orange-600">Response SLA</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="text-yellow-600 font-bold text-sm mb-1">P2 - Medium</div>
                        <div className="text-2xl font-bold text-yellow-700">4 hours</div>
                        <div className="text-sm text-yellow-600">Response SLA</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="text-blue-600 font-bold text-sm mb-1">P3 - Low</div>
                        <div className="text-2xl font-bold text-blue-700">24 hours</div>
                        <div className="text-sm text-blue-600">Response SLA</div>
                    </div>
                </div>
            </div>

            {/* Report Security Issue */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-8 border border-red-200">
                <div className="flex items-start space-x-4">
                    <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Report a Security Vulnerability</h3>
                        <p className="text-gray-700 mb-4">
                            If you discover a security vulnerability, please report it responsibly to our security team.
                        </p>
                        <a
                            href="mailto:security@linkboxai.com"
                            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                        >
                            <Shield className="h-5 w-5 mr-2" />
                            Contact Security Team
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

// HIPAA Compliance Tab
function HIPAACompliance() {
    return (
        <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">HIPAA Compliance Program</h2>
                <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                    LinkBox AI is committed to achieving full HIPAA compliance for handling Protected Health
                    Information (PHI). Our comprehensive compliance program addresses all Technical, Administrative,
                    Physical, and Breach Notification safeguards required by the HIPAA Security Rule.
                </p>

                {/* Progress Timeline */}
                <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Implementation Progress</h3>
                    <div className="space-y-4">
                        <PhaseProgress
                            phase="Phase 1: Technical Safeguards"
                            progress={40}
                            status="in-progress"
                            completedItems={[
                                'Immutable Audit Logging (6-year retention)',
                                'Session Timeout Enforcement (15 min)',
                                'Database Row-Level Security (194 tables)',
                                'AES-256 Encryption at Rest',
                                'TLS 1.3 Encryption in Transit',
                            ]}
                            upcomingItems={[
                                'Multi-Factor Authentication (Week 4)',
                                'Role-Based Access Control (Weeks 7-8)',
                                'End-to-End Encryption for PHI (Weeks 9-10)',
                            ]}
                        />
                        <PhaseProgress
                            phase="Phase 2: Administrative Safeguards"
                            progress={0}
                            status="starting"
                            completedItems={[]}
                            upcomingItems={[
                                'Business Associate Agreements (Week 3)',
                                'Security & Privacy Officers Assignment (Week 3)',
                                'Written HIPAA Policies (Week 3)',
                                'Annual Risk Assessment (Week 5)',
                                'Staff HIPAA Training (Week 6)',
                            ]}
                        />
                        <PhaseProgress
                            phase="Phase 3: Physical Safeguards"
                            progress={0}
                            status="planned"
                            completedItems={[]}
                            upcomingItems={[
                                'Device Encryption Documentation',
                                'Secure Disposal Procedures',
                                'Facility Access Controls',
                            ]}
                        />
                        <PhaseProgress
                            phase="Phase 4: Breach Notification"
                            progress={0}
                            status="planned"
                            completedItems={[]}
                            upcomingItems={[
                                'Incident Response Team Formation',
                                'Breach Detection Monitoring',
                                'HHS Breach Portal Setup',
                            ]}
                        />
                    </div>
                </div>

                {/* Compliance Timeline */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Compliance Timeline</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <div className="text-sm text-gray-600 mb-1">Program Start</div>
                            <div className="text-lg font-bold text-gray-900">January 2026</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 mb-1">Current Progress</div>
                            <div className="text-lg font-bold text-blue-600">16% Complete (Week 3 of 12)</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 mb-1">Target Completion</div>
                            <div className="text-lg font-bold text-green-600">April 18, 2026</div>
                        </div>
                    </div>
                </div>

                {/* Business Associate Agreements */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Business Associate Agreements</h3>
                    <p className="text-gray-700 mb-4">
                        We maintain Business Associate Agreements (BAAs) with all third-party vendors that
                        process Protected Health Information on our behalf:
                    </p>
                    <div className="space-y-2">
                        <BAAStatus vendor="Supabase (Database)" status="in-progress" />
                        <BAAStatus vendor="Google Cloud Platform (Vertex AI)" status="in-progress" />
                        <BAAStatus vendor="OpenAI (if used for PHI)" status="under-review" />
                    </div>
                </div>
            </div>

            {/* Compliance Contacts */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">HIPAA Compliance Contacts</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-lg p-6">
                        <div className="font-bold text-lg text-gray-900 mb-2">Security Officer</div>
                        <div className="text-gray-700 mb-4">Responsible for technical safeguards and security controls</div>
                        <a href="mailto:security@linkboxai.com" className="text-blue-600 hover:underline">
                            security@linkboxai.com
                        </a>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-6">
                        <div className="font-bold text-lg text-gray-900 mb-2">Privacy Officer</div>
                        <div className="text-gray-700 mb-4">Responsible for HIPAA policies and privacy compliance</div>
                        <a href="mailto:privacy@linkboxai.com" className="text-blue-600 hover:underline">
                            privacy@linkboxai.com
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Certifications Tab
function Certifications() {
    return (
        <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Certifications & Audits</h2>

                <div className="space-y-6">
                    <CertificationCard
                        title="HIPAA Compliance"
                        status="in-progress"
                        progress={16}
                        description="Comprehensive HIPAA Security and Privacy Rule compliance program"
                        targetDate="April 2026"
                        details={[
                            'Phase 1 Technical Safeguards: 40% complete',
                            'Independent third-party audit scheduled: Week 12',
                            'Annual risk assessment: Week 5',
                            'Full compliance target: April 18, 2026',
                        ]}
                    />

                    <CertificationCard
                        title="SOC 2 Type II"
                        status="planned"
                        progress={0}
                        description="Service Organization Control 2 certification for security, availability, and confidentiality"
                        targetDate="Q3 2026"
                        details={[
                            'Initial gap analysis: Planned Q2 2026',
                            'Audit period: 6 months observation required',
                            'Target certification: Q3 2026',
                        ]}
                    />

                    <CertificationCard
                        title="ISO 27001"
                        status="planned"
                        progress={0}
                        description="International standard for information security management systems"
                        targetDate="Q4 2026"
                        details={[
                            'Roadmap development: Q2 2026',
                            'Implementation: Q3 2026',
                            'Certification audit: Q4 2026',
                        ]}
                    />
                </div>
            </div>

            {/* Penetration Testing */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Testing & Audits</h3>
                <div className="space-y-4">
                    <div className="border-l-4 border-blue-600 bg-blue-50 p-4 rounded">
                        <div className="font-bold text-gray-900 mb-1">Penetration Testing</div>
                        <div className="text-gray-700 mb-2">
                            Independent security penetration testing scheduled for Week 11 (March 2026)
                        </div>
                        <div className="text-sm text-gray-600">Third-party security audit of all systems</div>
                    </div>

                    <div className="border-l-4 border-green-600 bg-green-50 p-4 rounded">
                        <div className="font-bold text-gray-900 mb-1">Vulnerability Scanning</div>
                        <div className="text-gray-700 mb-2">
                            Automated vulnerability scanning on all production systems (continuous)
                        </div>
                        <div className="text-sm text-gray-600">Tools: TBD (Commercial scanner + open-source)</div>
                    </div>

                    <div className="border-l-4 border-purple-600 bg-purple-50 p-4 rounded">
                        <div className="font-bold text-gray-900 mb-1">Code Security Review</div>
                        <div className="text-gray-700 mb-2">
                            All PHI-related code reviewed for security before deployment
                        </div>
                        <div className="text-sm text-gray-600">Mandatory code reviews + automated SAST scanning</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Downloads Tab
function Downloads() {
    return (
        <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Documentation & Resources</h2>
                <p className="text-gray-700 mb-8">
                    Access our security policies, compliance documentation, and audit reports.
                </p>

                <div className="space-y-4">
                    <DocumentDownload
                        title="HIPAA Compliance Roadmap"
                        description="12-week plan to full HIPAA compliance with detailed implementation guides"
                        size="31 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_COMPLIANCE_ROADMAP.md"
                    />
                    <DocumentDownload
                        title="HIPAA Policy Templates"
                        description="5 comprehensive policies: Breach Response, Access Control, Data Retention, Disaster Recovery, Incident Response"
                        size="14 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_POLICY_TEMPLATES.md"
                    />
                    <DocumentDownload
                        title="Security & Privacy Officers Assignment"
                        description="Role definitions, responsibilities, and training requirements"
                        size="9 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_OFFICERS.md"
                    />
                    <DocumentDownload
                        title="Business Associate Agreement Tracking"
                        description="Vendor BAA status, audit checklist, and compliance procedures"
                        size="12 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_BAA_TRACKING.md"
                    />
                    <DocumentDownload
                        title="Annual Risk Assessment Guide"
                        description="HHS SRA Tool implementation guide with LinkBox-specific examples"
                        size="18 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_RISK_ASSESSMENT_GUIDE.md"
                    />
                    <DocumentDownload
                        title="HIPAA Training Materials"
                        description="Comprehensive staff training program with quiz and attestation forms"
                        size="20 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_TRAINING_MATERIALS.md"
                    />
                    <DocumentDownload
                        title="HIPAA Email Templates"
                        description="Ready-to-send emails for vendor BAAs, officer assignments, and staff communications"
                        size="21 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_EMAIL_TEMPLATES.md"
                    />
                    <DocumentDownload
                        title="HIPAA FAQ - Comprehensive Q&A"
                        description="60+ frequently asked questions covering all aspects of HIPAA compliance"
                        size="19 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_FAQ.md"
                    />
                    <DocumentDownload
                        title="HIPAA Master Index"
                        description="Complete navigation guide and document library overview"
                        size="12 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_MASTER_INDEX.md"
                    />
                    <DocumentDownload
                        title="HIPAA Quick Start Guide"
                        description="Get started in 5 minutes - Week 3 day-by-day execution plan"
                        size="6 KB"
                        updated="2026-01-24"
                        href="/downloads/HIPAA_QUICK_START.md"
                    />
                    <DocumentDownload
                        title="Privacy Policy"
                        description="Our commitment to protecting your privacy and personal information"
                        size="TBD"
                        updated="2026-01-24"
                        href="/privacy"
                    />
                    <DocumentDownload
                        title="Terms of Service"
                        description="Terms and conditions for using LinkBox AI services"
                        size="TBD"
                        updated="2026-01-24"
                        href="/terms"
                    />
                </div>
            </div>

            {/* Audit Reports */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Audit Reports</h3>
                <p className="text-gray-700 mb-6">
                    Independent third-party audit reports will be available upon completion of certification.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <div className="text-gray-600">Audit reports coming Q2-Q4 2026</div>
                    <div className="text-sm text-gray-500 mt-2">
                        HIPAA Audit (April 2026) • SOC 2 Type II (Q3 2026) • ISO 27001 (Q4 2026)
                    </div>
                </div>
            </div>
        </div>
    );
}

// Helper Components
type StatusType = 'deployed' | 'in-progress' | 'planned';

function SecurityFeature({ icon, title, description, status }: { icon: React.ReactNode; title: string; description: string; status: StatusType }) {
    const statusColors: Record<StatusType, string> = {
        deployed: 'bg-green-50 border-green-200 text-green-700',
        'in-progress': 'bg-yellow-50 border-yellow-200 text-yellow-700',
        planned: 'bg-blue-50 border-blue-200 text-blue-700',
    };

    const statusLabels: Record<StatusType, string> = {
        deployed: 'Deployed',
        'in-progress': 'In Progress',
        planned: 'Planned',
    };

    return (
        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
                <div>{icon}</div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status]}`}>
                    {statusLabels[status]}
                </span>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
            <p className="text-gray-600 text-sm">{description}</p>
        </div>
    );
}

function ControlRow({ label, detail, status }: { label: string; detail: string; status: StatusType }) {
    const statusIcons: Record<StatusType, React.ReactNode> = {
        deployed: <CheckCircle className="h-5 w-5 text-green-600" />,
        'in-progress': <Clock className="h-5 w-5 text-yellow-600" />,
        planned: <AlertTriangle className="h-5 w-5 text-blue-600" />,
    };

    return (
        <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
            <div className="flex-1">
                <div className="font-medium text-gray-900">{label}</div>
                <div className="text-sm text-gray-600">{detail}</div>
            </div>
            <div>{statusIcons[status]}</div>
        </div>
    );
}

type PhaseStatusType = 'in-progress' | 'starting' | 'planned' | 'complete';

function PhaseProgress({ phase, progress, status, completedItems, upcomingItems }: { phase: string; progress: number; status: PhaseStatusType; completedItems: string[]; upcomingItems: string[] }) {
    const statusColors: Record<PhaseStatusType, string> = {
        'in-progress': 'bg-yellow-500',
        starting: 'bg-blue-500',
        planned: 'bg-gray-300',
        complete: 'bg-green-500',
    };

    return (
        <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-gray-900">{phase}</h4>
                <span className="text-sm font-semibold text-gray-600">{progress}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                    className={`h-2 rounded-full ${statusColors[status]}`}
                    style={{ width: `${progress}%` }}
                />
            </div>
            {completedItems.length > 0 && (
                <div className="mb-4">
                    <div className="text-sm font-semibold text-green-700 mb-2">✅ Completed:</div>
                    <ul className="space-y-1">
                        {completedItems.map((item: string, i: number) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start">
                                <span className="text-green-600 mr-2">•</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {upcomingItems.length > 0 && (
                <div>
                    <div className="text-sm font-semibold text-blue-700 mb-2">📋 Upcoming:</div>
                    <ul className="space-y-1">
                        {upcomingItems.map((item: string, i: number) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

type BAAStatusType = 'signed' | 'in-progress' | 'under-review';

function BAAStatus({ vendor, status }: { vendor: string; status: BAAStatusType }) {
    const statusColors: Record<BAAStatusType, string> = {
        signed: 'bg-green-50 border-green-200 text-green-700',
        'in-progress': 'bg-yellow-50 border-yellow-200 text-yellow-700',
        'under-review': 'bg-blue-50 border-blue-200 text-blue-700',
    };

    const statusLabels: Record<BAAStatusType, string> = {
        signed: 'Signed',
        'in-progress': 'In Progress',
        'under-review': 'Under Review',
    };

    return (
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <span className="font-medium text-gray-900">{vendor}</span>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status]}`}>
                {statusLabels[status]}
            </span>
        </div>
    );
}

type CertStatusType = 'complete' | 'in-progress' | 'planned';

function CertificationCard({ title, status, progress, description, targetDate, details }: { title: string; status: CertStatusType; progress: number; description: string; targetDate: string; details: string[] }) {
    const statusColors: Record<CertStatusType, string> = {
        complete: 'bg-green-50 border-green-200 text-green-700',
        'in-progress': 'bg-yellow-50 border-yellow-200 text-yellow-700',
        planned: 'bg-blue-50 border-blue-200 text-blue-700',
    };

    const statusLabels: Record<CertStatusType, string> = {
        complete: 'Complete',
        'in-progress': 'In Progress',
        planned: 'Planned',
    };

    return (
        <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h4 className="font-bold text-xl text-gray-900 mb-1">{title}</h4>
                    <p className="text-gray-600">{description}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColors[status]}`}>
                    {statusLabels[status]}
                </span>
            </div>

            {progress > 0 && (
                <>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <div className="text-sm text-gray-600 mb-4">{progress}% Complete</div>
                </>
            )}

            <div className="mb-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">Target Completion:</div>
                <div className="text-sm text-gray-600">{targetDate}</div>
            </div>

            <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">Details:</div>
                <ul className="space-y-1">
                    {details.map((detail: string, i: number) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            {detail}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function DocumentDownload({ title, description, size, updated, href }: { title: string; description: string; size: string; updated: string; href: string }) {
    return (
        <a
            href={href}
            download
            className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all group"
        >
            <div className="flex items-start space-x-4 flex-1">
                <FileText className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                    <div className="font-semibold text-gray-900 group-hover:text-blue-600">{title}</div>
                    <div className="text-sm text-gray-600 mt-1">{description}</div>
                    <div className="flex space-x-4 mt-2 text-xs text-gray-500">
                        <span>Size: {size}</span>
                        <span>•</span>
                        <span>Updated: {updated}</span>
                    </div>
                </div>
            </div>
            <Download className="h-5 w-5 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
        </a>
    );
}
